import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

/**
/**
 * Fetches delivery note attachment data from the API.
 * @param chainId - The ID of the delivery note chain.
 * @param digest - The digest of the attachment.
 * @returns The attachment data as a blob if successful.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_delivery_note_attachment_asset_delivery_note__id__attachment_get
 */
export const fetchAttachment = async (chainId: UUID, digest: string) => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_ATTACHMENT(chainId, digest))
    .blob();

  return response;
};

/**
 * React Query based custom hook for getting an attachment for a delivery note.
 * @param chainId - The ID of the delivery note chain.
 * @param digest - The digest of the attachment.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryAttachment = <TSelected = Blob>(
  { chainId, digest }: { chainId: UUID; digest: string },
  options?: Omit<
    UseQueryOptions<Blob, unknown, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<Blob, unknown, TSelected>({
    enabled: Boolean(chainId) && Boolean(digest),
    gcTime: ms('10m'),
    queryFn: async () => fetchAttachment(chainId, digest),
    queryKey: queryKeysDeliveryNote.getAttachment(chainId, digest),
    staleTime: Infinity,
    ...options,
  });
